import React from "react";
import PropTypes from "prop-types";
import useCurrentDealStore from "stores/DealStore";
import useCurrentSaleStore from "stores/SaleStore";
import shallow from "zustand/shallow";
import { Button } from "reactstrap";
import { submitLoanApplication } from "../../../api/AppTrackerAPI.js"; // adjust path as needed
import Swal from "sweetalert2";
import { REQUIRED_FIELDS } from "../../loanManager/compontents/fieldLabels";

import AppTrackerDataDisplay from "./AppTrackerDataDisplay";

const AppTrackerDealData = ({ onAppSubmit }) => {
	// Deal store
	const {
		deal,
		buyer,
		buyerAddress,
		buyerEmployments,
		coBuyer,
		coBuyerAddress,
		coBuyerEmployments,
		vehicle,
		location,
	} = useCurrentDealStore(
		(state) => ({
			deal: state.deal,
			buyer: state.buyer,
			buyerAddress: state.buyerAddress,
			buyerEmployments: state.buyerEmploymentHistory,
			coBuyer: state.coBuyer,
			coBuyerAddress: state.coBuyerAddress,
			coBuyerEmployments: state.coBuyerEmploymentHistory,
			vehicle: state.vehicle,
			location: state.location,
		}),
		shallow
	);

	// Get sale information
	const {
		term,
		price,
		down,
		docFee,
		licenseFee,
		tax,
		amtFinanced,
		regularPmt,
		apr,
		trade,
		gap,
		warranty,
	} = useCurrentSaleStore(
		(state) => ({
			term: state.term,
			price: state.price,
			down: state.down,
			docFee: state.docFee,
			licenseFee: state.licenseFee,
			tax: state.tax,
			amtFinanced: state.amtFinanced,
			regularPmt: state.regularPmt,
			apr: state.apr,
			trade: state.trade,
			gap: state.gap,
			warranty: state.warranty,
		}),
		shallow
	);

	const borrowerData = buyer && {
		Borrower: {
			Ssn: buyer.ssn || "111111111",
			Type: "borrower",
			FirstName: buyer.firstName,
			MiddleName: buyer.middleName,
			LastName: buyer.lastName,
			Dob: buyer.birthDate,
			IsUsCitizen: buyer.licCountry === "USA" ? "Y" : "N",
			CellPhone: buyer.cellPhone,
		},
	};

	const borrowerAddressData = Array.isArray(buyerAddress)
		? [
				{
					BorrowerAddressLoanReq: buyerAddress.map((address) => ({
						Type: "homeaddress",
						Status: address.isCurrent ? "current" : "previous",
						Address: address.street,
						City: address.city,
						State: address.state,
						ZipCode: address.zip,
						TimeAtResYears: 2,
						TimeAtResMonths: address.months || 4,
						OwnershipType: address.ownership,
						MonthlyPmt: address.monthlyPayment,
					})),
				},
		  ]
		: buyerAddress
		? [
				{
					BorrowerAddressLoanReq: [
						{
							Type: "homeaddress",
							Status: buyerAddress.isCurrent ? "current" : "previous",
							Address: buyerAddress.street,
							City: buyerAddress.city,
							State: buyerAddress.state,
							ZipCode: buyerAddress.zip,
							TimeAtResYears: buyerAddress.timeAtResYears || 0,
							TimeAtResMonths: buyerAddress.timeAtResMonths || 0,
							OwnershipType: buyerAddress.ownership,
							MonthlyPmt: buyerAddress.monthlyPayment,
						},
					],
				},
		  ]
		: [];

	const borrowerEmploymentsData = Array.isArray(buyerEmployments)
		? {
				BorrowerEmployments: buyerEmployments.map((employment) => ({
					CurrentEmploymnt: employment.isCurrent ? "Y" : "N",
					Status: employment.employmentStatus,
					Name: employment.company,
					ContactPhone: employment.phone,
					JobPos: employment.position,
					TimeEmpYears: employment.years,
					TimeEmpMonths: employment.months,
					GrossMonthlyInc: parseFloat(employment.salary),
					Income: 0,
					Period: "bi-weekly",
				})),
		  }
		: buyerEmployments
		? {
				BorrowerEmployments: [
					{
						CurrentEmploymnt: buyerEmployments.isCurrent ? "Y" : "N",
						Status: buyerEmployments.employmentStatus,
						Name: buyerEmployments.company,
						ContactPhone: buyerEmployments.phone,
						JobPos: buyerEmployments.position,
						TimeEmpYears: buyerEmployments.years,
						TimeEmpMonths: buyerEmployments.months,
						GrossMonthlyInc: parseFloat(buyerEmployments.salary),
						Income: 0,
						Period: "bi-weekly",
					},
				],
		  }
		: {};

	const coBorrowerAge = coBuyer.birthDate
		? new Date().getFullYear() - parseInt(coBuyer.birthDate.split("-")[0], 10)
		: 0;

	const coBorrowerData = coBuyer && {
		CoBorrower: {
			Ssn: coBuyer.Ssn || "222222222",

			Type: "coborrower",
			FirstName: coBuyer.firstName,
			LastName: coBuyer.lastName,
			Dob: coBuyer.birthDate,
			Age: coBorrowerAge,
			IsUsCitizen: coBuyer.licCountry === "USA" ? "Y" : "N",
			HomePhone: coBuyer.cellPhone,
		},
	};

	const coBorrowerAddressData = Array.isArray(coBuyerAddress)
		? [
				{
					CoBorrowerAddressLoanReq: coBuyerAddress.map((address) => ({
						Type: "homeaddress",
						Status: address.isCurrent ? "current" : "previous",
						Address: address.street,
						City: address.city,
						State: address.state,
						ZipCode: address.zip,
						TimeAtResYears: address.years,
						TimeAtResMonths: address.months,
						OwnershipType: address.ownership,
						MonthlyPmt: address.monthlyPayment,
					})),
				},
		  ]
		: coBuyerAddress
		? [
				{
					CoBorrowerAddressLoanReq: [
						{
							Type: "homeaddress",
							Status: coBuyerAddress.isCurrent ? "current" : "previous",
							Address: coBuyerAddress.street,
							City: coBuyerAddress.city,
							State: coBuyerAddress.state,
							ZipCode: coBuyerAddress.zip,
							TimeAtResYears: coBuyerAddress.years,
							TimeAtResMonths: coBuyerAddress.months || 0,
							OwnershipType: coBuyerAddress.ownership,
							MonthlyPmt: coBuyerAddress.monthlyPayment,
						},
					],
				},
		  ]
		: [];

	const coBorrowerEmploymentsData = Array.isArray(coBuyerEmployments)
		? {
				CoBorrowerEmployments: coBuyerEmployments.map((employment) => ({
					CurrentEmploymnt: employment.isCurrent ? "Y" : "N",
					Status: employment.employmentStatus,
					Name: employment.company,
					ContactPhone: employment.phone,
					JobPos: employment.position,
					TimeEmpYears: employment.years,
					TimeEmpMonths: employment.months,
					GrossMonthlyInc: parseFloat(employment.salary),
					Income: parseFloat(employment.salary),
					Period: employment.salaryType,
				})),
		  }
		: coBuyerEmployments
		? {
				CoBorrowerEmployments: [
					{
						CurrentEmploymnt: coBuyerEmployments.isCurrent ? "Y" : "N",
						Status: coBuyerEmployments.employmentStatus,
						Name: coBuyerEmployments.company,
						ContactPhone: coBuyerEmployments.phone,
						JobPos: coBuyerEmployments.position,
						TimeEmpYears: coBuyerEmployments.years,
						TimeEmpMonths: coBuyerEmployments.months,
						GrossMonthlyInc: parseFloat(coBuyerEmployments.salary),
						Income: parseFloat(coBuyerEmployments.salary),
						Period: "bi-weekly",
					},
				],
		  }
		: {};

	const isMissingCobuyerInfo =
		!coBuyer ||
		!coBuyerAddress ||
		(Array.isArray(coBuyerAddress) && coBuyerAddress.length === 0) ||
		!coBuyerEmployments ||
		(typeof coBuyerEmployments === "object" &&
			Object.keys(coBuyerEmployments).length === 0);

	const loanRequestVehicleData = vehicle && {
		LoanRequestVehicle: {
			State: location.state,
			EvalType: "NADA", // Hardcoded as per your example
			ZipCode: parseInt(location.zipCode, 10), // Assuming 'zipcode' is available
			Vin: vehicle.vin,
			VehicleYear: parseInt(vehicle.year, 10),
			EvalDate: new Date().toISOString().split("T")[0], // Using current date as EvalDate
			VehicleTrim: vehicle.subModel, // Using subModel as a close match
			Make: vehicle.make,
			Mileage: parseInt(vehicle.odometerIn, 10),
			Model: vehicle.model,
			Bookflag: "U", // Hardcoded as per your example
			VehicleClass: vehicle.newUsed === "Used" ? "UsedCar" : "NewCar", // Basic mapping
			Mode: "external", // Hardcoded as per your example
			Manual: vehicle.transmission?.toLowerCase().includes("manual")
				? "Y"
				: "N", // Basic check
			BaseValue1: 0,
			MileageAdjValue1: 0,
			OptionsValue1: 0,
			TotalValue1: 0,
			ValueType1: "TradeIn",
			BaseValue2: 0,
			MileageAdjValue2: 0,
			OptionsValue2: 0,
			TotalValue2: 0,
		},
	};

	const loanRequestData = {
		LoanRequest: {
			FinTypeCode: "auto_installment_indirect", // Hardcoded
			Term: term,
			NbrPayments: term, // Assuming term is in months and equals number of payments
			TotalCashPrice: price,
			DownPmt: down,
			DocFee: docFee,
			LicenseTagsReg: licenseFee, // Assuming licenseFee covers tags and registration
			SalesTax: tax,
			AmtToFin: amtFinanced,
			ReqPmt: regularPmt,
			PmtFreq: "monthly", // Assuming monthly payments
			FicoScore: buyer?.creditScore || 0, // Assuming buyer has creditScore
			TotalDebt: 0, // Placeholder
			TradeInAllow: trade,
			ServContractCode: warranty > 0 ? "yes" : "none", // Basic check
			ServContractAmt: warranty,
			InterestRate: apr * 100, // Convert APR to percentage
			GapCode: gap > 0 ? "yes" : "none", // Basic check
			InsuranceTerm: 0, // Placeholder
			InsuranceAmt: 0, // Placeholder
			GapInsAmt: gap,
			OtherTaxable: 0, // Placeholder
			OtherNonTaxable: 0, // Placeholder
			PayoffOnTrade: 0, // Placeholder - if you didn't remove trade completely
			Rebate: 0, // Placeholder
		},
	};

	const fullApplicationData = {
		...borrowerData,
		...borrowerEmploymentsData,
		BorrowerAddress: borrowerAddressData,
		...(!isMissingCobuyerInfo && {
			...coBorrowerData,
			...coBorrowerEmploymentsData,
			CoBorrowerAddress: coBorrowerAddressData,
		}),
		...loanRequestVehicleData,
		...loanRequestData,
	};

	const payloadToSubmit = {
		params: {
			json: {
				Comp: "na",
				DealerId: "87878787",
				Source: "ds",
				Type: "joint",
				AppDate: new Date().toISOString().replace("T", " ").slice(0, 19),
				PaymentCall: "N",
				...fullApplicationData,
			},
		},
		action: "SaveSubmit",
		callback_url: "https://goroutes.dealerclick.com/loan-application/status",
	};

	const dealerID = `${deal.dealerID}${deal.locationID}`;
	const dealID = deal.ID;
	console.log("dealerID", dealerID);

	const handleSubmit = async () => {
		console.log("Submitted Application:", payloadToSubmit);

		try {
			const response = await submitLoanApplication(payloadToSubmit, {
				dealerId: dealerID,
				lenderId: "AppTracker",
				dealId: dealID,
			});

			console.log("Application successfully submitted:", response);

			await Swal.fire({
				icon: "success",
				title: "Application Submitted",
				text: "The loan application was submitted successfully.",
				confirmButtonColor: "#28a745",
			});

			// 🔁 Notify parent to re-fetch loans after confirmation
			if (typeof onAppSubmit === "function") {
				onAppSubmit();
			}
		} catch (error) {
			console.error("Failed to submit application:", error);

			await Swal.fire({
				icon: "error",
				title: "Submission Failed",
				text: "Something went wrong. Please try again or check the console.",
				confirmButtonColor: "#dc3545",
			});
		}
	};

	return (
		<div>
			<p
				className="text-center text-danger mb-5"
				style={{ marginTop: "-1.2rem" }}
			>
				Please review the data below, make any necessary changes in the
				appropriate module, and return here to preview before sending. All
				fields are required except cobuyer details.
			</p>
			<AppTrackerDataDisplay
				title="Borrower Details"
				data={borrowerData.Borrower}
				requiredFields={REQUIRED_FIELDS.Borrower}
			/>
			<AppTrackerDataDisplay
				title="Borrower Address(s)"
				data={borrowerAddressData[0]?.BorrowerAddressLoanReq}
				requiredFields={REQUIRED_FIELDS.BorrowerAddressLoanReq}
			/>
			<AppTrackerDataDisplay
				title="Borrower Employment(s)"
				data={borrowerEmploymentsData.BorrowerEmployments}
				requiredFields={REQUIRED_FIELDS.BorrowerEmployments}
			/>

			{isMissingCobuyerInfo ? (
				<></>
			) : (
				<>
					<AppTrackerDataDisplay
						title="Co-Borrower Details"
						data={coBorrowerData.CoBorrower}
						requiredFields={REQUIRED_FIELDS.CoBorrower}
					/>
					<AppTrackerDataDisplay
						title="Co-Borrower Address(s)"
						data={coBorrowerAddressData[0]?.CoBorrowerAddressLoanReq}
						requiredFields={REQUIRED_FIELDS.CoBorrowerAddressLoanReq}
					/>
					<AppTrackerDataDisplay
						title="Co-Borrower Employment(s)"
						data={coBorrowerEmploymentsData.CoBorrowerEmployments}
						requiredFields={REQUIRED_FIELDS.CoBorrowerEmployments}
					/>
				</>
			)}

			<AppTrackerDataDisplay
				title="Vehicle Information"
				data={loanRequestVehicleData.LoanRequestVehicle}
				requiredFields={REQUIRED_FIELDS.Vehicle}
			/>
			<AppTrackerDataDisplay
				title="Loan Request Information"
				data={loanRequestData.LoanRequest}
				requiredFields={REQUIRED_FIELDS.SaleInfo}
			/>
			<div className="d-flex justify-content-center mt-2 mb-3">
				<Button
					className="btn-success btn-md mt-3 mx-auto mb-4 text-center"
					onClick={handleSubmit}
				>
					<i className="nc-icon nc-check-2" /> Submit Application
				</Button>
			</div>
		</div>
	);
};

AppTrackerDealData.propTypes = {
	onAppSubmit: PropTypes.func.isRequired,
};

export default AppTrackerDealData;
